'use client'

import { useCallback, useEffect, useRef } from 'react'

interface Options {
    /** Altura inicial e mínima, em px. */
    minHeight?: number
    /** Teto: passando disso o campo ganha rolagem em vez de continuar crescendo. */
    maxHeight?: number
}

/**
 * Textarea que cresce com o conteúdo.
 *
 * Um `<textarea rows={1}>` com altura fixa não cresce: o texto que passa da
 * primeira linha some para dentro da rolagem. Quem está escrevendo perde de
 * vista o que já escreveu e conclui que o campo "não aceita quebra de linha" —
 * quando na verdade só não estava mostrando.
 *
 * O ajuste precisa rodar também quando o valor muda POR FORA da digitação
 * (envio que limpa o campo, texto inserido por atalho, rascunho carregado), por
 * isso o hook observa `value` em vez de depender só do `onChange`.
 */
export function useAutoResizeTextarea(
    value: string,
    { minHeight = 96, maxHeight = 240 }: Options = {}
) {
    const ref = useRef<HTMLTextAreaElement | null>(null)

    const resize = useCallback(() => {
        const el = ref.current
        if (!el) return

        // Zera antes de medir: sem isso o scrollHeight nunca diminui e o campo
        // fica preso na maior altura que já teve.
        el.style.height = 'auto'
        const target = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)
        el.style.height = `${target}px`
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
    }, [minHeight, maxHeight])

    useEffect(() => {
        resize()
    }, [value, resize])

    return { ref, resize }
}
